import { _isNumberValue } from "@angular/cdk/coercion";
import {
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
  VIRTUAL_SCROLL_STRATEGY,
} from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  TrackByFunction,
  booleanAttribute,
  computed,
  contentChild,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";

import { NoResults } from "@bitwarden/assets/svg";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { I18nPipe } from "@bitwarden/ui-common";

import { CheckboxModule } from "../../checkbox";
import { DialogService } from "../../dialog";
import { FILTER_HOST, FilterControl, FilterHost } from "../../filter-menu/filter-tokens";
import { IconComponent } from "../../icon/icon.component";
import { ItemComponent } from "../../item/item.component";
import { ScrollLayoutService } from "../../layout/scroll-layout.directive";
import { SearchComponent } from "../../search/search.component";
import { SkeletonTextComponent } from "../../skeleton";
import { StatusLockupComponent } from "../../status-lockup/status-lockup.component";
import { SvgComponent } from "../../svg";
import { TooltipDirective } from "../../tooltip";
import { ParamState, ParamValue, queryParamStore } from "../../utils";
import { SortDirection, SortFn } from "../table-data-source";

import { BitCellComponent } from "./bit-cell.component";
import { BitColumnComponent } from "./bit-column.component";
import { BitHeaderRowComponent } from "./bit-header-row.component";
import { BitRowGroupComponent } from "./bit-row-group.component";
import { BitRowComponent } from "./bit-row.component";
import { BitTablePaginatorComponent, TableRowRange } from "./bit-table-paginator.component";
import { ColumnName } from "./column";
import {
  CustomizeColumnsDialogComponent,
  CustomizeColumnsDialogParams,
} from "./customize-columns-dialog.component";
import { SortState, cycleSort } from "./sort-model";
import { SyncScrollLeftDirective } from "./sync-scroll-left.directive";
import { TableColumnPreferencesService } from "./table-column-preferences.service";
import { TableDef } from "./table-def";
import { TABLE_PRESENTATION, TablePresentation } from "./table-presentation";
import { TableSelectionConfig, TableSelectionModel } from "./table-selection-model";
import { TableStateKey } from "./table-state-keys";
import { TableVirtualScrollStrategy } from "./table-virtual-scroll.strategy";

/** Grid track width for the internal selection column: the 24px checkbox plus the cell's `tw-px-4`. */
const SELECTION_COLUMN_WIDTH = "56px";

/** The `min` of a `minmax(min, max)` track. */
const MINMAX_MIN = /^minmax\(\s*([^,]+?)\s*,/;

/**
 * Whether a track can absorb the row's leftover width: only an `fr` max grows. `fr` is invalid
 * as a `minmax` min, so a trailing `fr` can only be the max.
 */
function growsToFill(width: string): boolean {
  return /fr\)?$/.test(width.trim());
}

/** The same track with its minimum intact and its max freed: `240px` becomes `minmax(240px, 1fr)`. */
function grown(width: string): string {
  return `minmax(${MINMAX_MIN.exec(width)?.[1] ?? width.trim()}, 1fr)`;
}

/**
 * Fixed heights (px) of group headers when virtualized. Must match the header chrome
 * in {@link BitTableV2Component.groupHeaderClass}.
 */
const GROUP_HEADER_HEIGHT = 28;
const SUBGROUP_HEADER_HEIGHT = 22;

/**
 * Fixed height (px) of a group description when virtualized: two `text-sm` lines (20px
 * each) plus the cell's `tw-pb-2`. Descriptions are consumer-supplied localized strings,
 * so they wrap in narrow tables even where English doesn't — two lines is the allowance,
 * and the cell clamps past it. The strategy never measures, so every description gets
 * this height whether or not it wraps.
 */
const GROUP_DESCRIPTION_HEIGHT = 48;

/** The `filterValues` key a projected `bit-search`'s term is adopted under. */
const SEARCH_FILTER_KEY = "search";

/** URL-sync param keys for the non-filter facets, alongside the filter keys in the namespace. */
const SORT_PARAM = "sort";
const DIRECTION_PARAM = "direction";
const PAGE_PARAM = "page";
const PAGE_SIZE_PARAM = "pageSize";

/** Selection config a consumer supplies; the table provides the `rows` scope itself. */
export type SelectionConfig<T> = Omit<TableSelectionConfig<T>, "rows">;

/** Reads a column's value for default sorting, coercing numeric strings to numbers. */
function sortAccessor<T>(row: T, column: string): string | number {
  const value = (row as Record<string, unknown>)[column];
  if (_isNumberValue(value)) {
    const num = Number(value);
    return num < Number.MAX_SAFE_INTEGER ? num : (value as string);
  }
  return value as string | number;
}

/**
 * Returns a sorted copy of `data` by `column`/`direction`, using `fn` when the
 * column supplies one. The default comparison (number/string coercion, null
 * handling) is ported from Angular Material's `MatTableDataSource` (MIT,
 * Copyright (c) 2024 Google LLC).
 */
function sortRows<T>(
  data: readonly T[],
  column: string,
  direction: SortDirection,
  fn: SortFn | undefined,
): T[] {
  const dirMod = direction === "asc" ? 1 : -1;
  return [...data].sort((a, b) => {
    if (fn) {
      return fn(a, b, direction) * dirMod;
    }

    let valueA = sortAccessor(a, column);
    let valueB = sortAccessor(b, column);

    // Coerce mismatched types to strings so they order consistently.
    const typeA = typeof valueA;
    const typeB = typeof valueB;
    if (typeA !== typeB) {
      if (typeA === "number") {
        valueA += "";
      }
      if (typeB === "number") {
        valueB += "";
      }
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      return valueA.localeCompare(valueB) * dirMod;
    }

    // Existing values sort before missing ones; equal/both-missing stay put.
    let result = 0;
    if (valueA != null && valueB != null) {
      if (valueA > valueB) {
        result = 1;
      } else if (valueA < valueB) {
        result = -1;
      }
    } else if (valueA != null) {
      result = 1;
    } else if (valueB != null) {
      result = -1;
    }
    return result * dirMod;
  });
}

/** A flattened body item: a data row, a group header with its row-group and count, or a group description. */
type RenderItem<T> =
  | { kind: "row"; row: T }
  | { kind: "group"; group: BitRowGroupComponent<T>; count: number; level: number }
  | { kind: "groupDescription"; group: BitRowGroupComponent<T>; level: number };

/**
 * **Beta.** `bit-table-v2` is still stabilizing. Do not adopt it in production
 * without approval from the UI Foundation team.
 */
@Component({
  selector: "bit-table-v2",
  exportAs: "bitTableV2",
  templateUrl: "./table-v2.component.html",
  // The header row scrolls horizontally in sync with the body; hide its own
  // scrollbar so the body owns the single visible one.
  styles: [
    `
      [data-hide-scrollbar] {
        scrollbar-width: none;
      }
      [data-hide-scrollbar]::-webkit-scrollbar {
        display: none;
      }
    `,
  ],
  imports: [
    CommonModule,
    CdkVirtualScrollViewport,
    CdkVirtualForOf,
    BitCellComponent,
    BitHeaderRowComponent,
    BitRowComponent,
    CheckboxModule,
    IconComponent,
    ItemComponent,
    StatusLockupComponent,
    SkeletonTextComponent,
    SvgComponent,
    SyncScrollLeftDirective,
    TooltipDirective,
    I18nPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // Filter chips projected into the table resolve this host by DI and
    // self-register; the table folds their values into `filtered`.
    { provide: FILTER_HOST, useExisting: forwardRef(() => BitTableV2Component) },
    {
      provide: TABLE_PRESENTATION,
      useFactory: (table: BitTableV2Component) => table.presentation,
      deps: [forwardRef(() => BitTableV2Component)],
    },
    // The virtual-scroll viewport in the template picks up the table's own strategy.
    {
      provide: VIRTUAL_SCROLL_STRATEGY,
      useFactory: (table: BitTableV2Component) => table.scrollStrategy,
      deps: [forwardRef(() => BitTableV2Component)],
    },
  ],
  host: {
    // In `fill` mode the host becomes a flex column that fills its parent's
    // height, so the table can hand a bounded height down to its scroll region.
    // The floor stops a crowded parent squeezing it to nothing; the page scrolls instead.
    "[class.tw-flex]": "isFill()",
    "[class.tw-flex-col]": "isFill()",
    "[class.tw-flex-1]": "isFill()",
    "[class.tw-min-h-64]": "isFill()",
  },
})
export class BitTableV2Component<T = unknown, S extends string = never, F = Record<string, unknown>>
  implements AfterContentInit, FilterHost
{
  protected readonly noResultsSvg = NoResults;
  /**
   * The typed contract — row type `T`, synthetic columns `S`, filter shape `F` — plus
   * the row data. See {@link defineTable}. Optional; manual-mode tables need not bind it.
   */
  readonly tableDef = input(new TableDef<T, S>(signal<T[]>([])));

  /**
   * The columns to display, in order. Omit to show every projected `<bit-column>` in
   * declaration order.
   */
  readonly displayedColumns = input<readonly ColumnName<T, S>[]>();

  /**
   * Render style. `"table"` draws the bordered column grid; `"list"` renders each row
   * as a standalone card.
   */
  readonly presentation = input<TablePresentation>("table");

  /** Active sort (`{ column, direction }`). Two-way — header clicks cycle it; bind `[(sort)]` to persist. */
  readonly sort = model<SortState<ColumnName<T, S>>>({ direction: "asc" });

  /** When `true`, the table shows skeleton rows in place of data (e.g. a resource's `isLoading`). */
  readonly loading = input(false, { transform: booleanAttribute });

  /**
   * Client-side row test, given a row and {@link filterValues}. `F` is inferred from
   * this fn's `values` parameter — annotate it (`(row, f: Filters) => …`) to type
   * `filterValues()`. Omit for server-side filtering.
   */
  readonly filter = input<(row: T, values: F) => boolean>();

  /** Initial filter values, keyed by chip `key`; seeds the matching chips once on init. */
  readonly filters = input<Partial<F>>();

  /** Row selection config. Omit for a non-selectable table (no checkbox column). */
  readonly selection = input<SelectionConfig<T>>();

  /** Emits the selected rows whenever the selection changes. */
  readonly selectedChange = output<readonly T[]>();

  /**
   * Fixed row height in pixels. Setting it turns the table into a virtual scroll
   * viewport; give columns explicit widths, and set {@link height}.
   */
  readonly virtualRowHeight = input<number>();

  /**
   * How tall the table is. Omit to grow to content. `"fill"` grows to the host's
   * height and scrolls the body within it. A number caps the body at that many rows
   * (minimum 4), and applies only when {@link virtualRowHeight} is set.
   */
  readonly height = input<"fill" | number>();

  /**
   * Registers the scrolling body as the page's scroll region, like `bitScrollLayoutHost`. A
   * `"fill"` table is what scrolls, since the layout's region wraps it exactly. `"fill"` only.
   */
  readonly scrollLayoutHost = input(false, { transform: booleanAttribute });

  /** Optional trackBy for the virtualized row list. */
  readonly trackBy = input<TrackByFunction<T>>();

  /** Number of skeleton rows to show while {@link loading} is true. */
  readonly loadingRows = input(3);

  /**
   * Syncs sort, filters, and pagination to the URL query string under this prefix —
   * e.g. `queryParam="vault"` yields `?vault.sort=name&vault.type=login`. On load,
   * params win over `[filters]` and `defaultSort`. A no-op without a router in context.
   */
  readonly queryParam = input<string>();

  /**
   * Namespaces this table's stored column preference, and opts it into customization.
   * Required once any `<bit-column>` is marked `removable` — without it there is
   * nowhere to persist the user's choice, so the Customize control stays hidden.
   *
   * Register new keys in `table-state-keys.ts`.
   */
  readonly stateKey = input<TableStateKey>();

  /**
   * The table's combined URL state, mirrored to the `queryParam` namespace. Seeds from
   * the URL on first read. A no-op signal when `queryParam` is unset or there's no router.
   */
  private readonly urlStore = queryParamStore<ParamState>(this.queryParam);

  /** Whether initial URL state has been applied — gates write-back until then. */
  private readonly urlRestored = signal(false);

  /** The paginator's consumer-configured page size; the `pageSize` param is omitted at this value. */
  private readonly baselinePageSize = signal<number | undefined>(undefined);

  /** A projected paginator, if any — owns the page state; the table reads it to slice. */
  private readonly paginator = contentChild(BitTablePaginatorComponent);

  /** Registered filter chips (from projection), the source of {@link filterValues}. */
  private readonly _filters = signal<readonly FilterControl[]>([]);

  /** Registered filter chips, exposed for initial-value seeding. */
  readonly filterControls = this._filters.asReadonly();

  /**
   * The chips' combined value, keyed by each chip's `key`. Drives {@link filter}, and
   * is what you read for a server query.
   */
  readonly filterValues = computed<F>(() => {
    const values: Record<string, unknown> = {};
    for (const control of this._filters()) {
      values[control.key()] = control.value();
    }
    return values as F;
  });

  /**
   * Rows passing {@link filter} given {@link filterValues}, pre-sort. Also the scope
   * for select-all and the paginator's total.
   */
  readonly filtered = computed<T[]>(() => {
    const filter = this.filter();
    const data = this.tableDef().data();
    if (!filter) {
      return data;
    }
    const values = this.filterValues();
    return data.filter((row) => filter(row, values));
  });

  /** The filtered row count — read by a projected `bit-table-paginator` for its total. */
  readonly filteredCount = computed(() => this.filtered().length);

  /**
   * The rows on screen — a projected paginator's page, else every filtered row. Read by a
   * projected `bit-table-toolbar` for its range label.
   */
  readonly rowRange = computed<TableRowRange>(() => {
    const paginator = this.paginator();
    if (paginator) {
      return paginator.range();
    }
    const total = this.filteredCount();
    return { start: total === 0 ? 0 : 1, end: total, total };
  });

  private readonly _selectionModel = signal<TableSelectionModel<T> | undefined>(undefined);

  /** Selection state, present only when {@link selection} is configured. */
  readonly selectionModel = this._selectionModel.asReadonly();

  /** Registers a projected filter chip. Called by the chip as it self-registers. */
  registerFilter(control: FilterControl): void {
    this._filters.update((filters) => [...filters, control]);
  }

  /** @see {@link registerFilter} */
  unregisterFilter(control: FilterControl): void {
    this._filters.update((filters) => filters.filter((f) => f !== control));
  }

  /**
   * Count for a chip option: rows matching {@link filter} with `key` pinned to `value` and every
   * other active filter applied. `undefined` with no `[filter]` (server-side) — the chip then
   * shows an explicit `count` instead.
   */
  optionCount(key: string, value: unknown): number | undefined {
    const filter = this.filter();
    if (!filter) {
      return undefined;
    }
    const values = { ...this.filterValues(), [key]: value } as F;
    return this.tableDef()
      .data()
      .filter((row) => filter(row, values)).length;
  }

  /** Chips already seeded from {@link filters}, so each is seeded at most once. */
  private readonly seeded = new WeakSet<FilterControl>();

  /**
   * A `bit-search` projected anywhere into the table. Adopted automatically, so its
   * term joins {@link filterValues} under {@link SEARCH_FILTER_KEY}.
   */
  private readonly search = contentChild(SearchComponent, { descendants: true });

  constructor() {
    // Adopt a projected `bit-search` as a `search` filter control.
    effect((onCleanup) => {
      const search = this.search();
      if (!search) {
        return;
      }
      const control: FilterControl = {
        key: signal(SEARCH_FILTER_KEY),
        value: search.value,
        clearedValue: signal(""),
        active: computed(() => (search.value() ?? "") !== ""),
        setValue: (value) => search.writeValue(value == null ? "" : String(value)),
      };
      this.registerFilter(control);
      onCleanup(() => this.unregisterFilter(control));
    });

    // Seed chips as they register and their keys resolve — from the URL store when
    // it holds a value for the key (a shared link restores faithfully), else from
    // `[filters]`. Reading the store seeds it from the URL synchronously. Each chip
    // is seeded once; later user edits aren't undone.
    effect(() => {
      const fromUrl = this.urlStore() as Record<string, unknown>;
      const initial = this.filters() as Record<string, unknown> | undefined;
      for (const control of this._filters()) {
        if (this.seeded.has(control)) {
          continue;
        }
        const key = control.key();
        if (!key) {
          continue;
        }
        let value: unknown;
        if (fromUrl[key] != null) {
          value = fromUrl[key];
        } else if (initial && key in initial) {
          value = initial[key];
        } else {
          continue;
        }
        this.seeded.add(control);
        control.setValue(value);
      }
    });

    /** (Re)build the selection model from config — in an effect, since the model's
     * constructor writes a signal (not allowed in a computed). Scoped over the rows in display
     * order (see {@link sorted}), so a capped select-all keeps the ones the user sees first.
     */
    effect(() => {
      const config = this.selection();
      this._selectionModel.set(
        config ? new TableSelectionModel<T>({ ...config, rows: this.sorted }) : undefined,
      );
    });

    // Surface the selection to the consumer as it changes.
    effect(() => {
      const model = this.selectionModel();
      if (model) {
        this.selectedChange.emit(model.selected());
      }
    });

    // Mirror the table's combined state back to the URL on change. Gated on
    // `urlRestored` so defaults don't clobber incoming params before they're read.
    // Inactive/default facets become empty values, which the store omits, so the
    // URL self-cleans. The store is a no-op when `queryParam` is unset.
    effect(() => {
      if (!this.urlRestored()) {
        return;
      }
      // Seed from the store's current value rather than starting empty, so a param
      // whose chip hasn't registered yet (e.g. one gated behind an `@if` that's still
      // waiting on data) survives this write instead of being dropped.
      const state: ParamState = { ...untracked(this.urlStore) };
      for (const control of this._filters()) {
        const key = control.key();
        if (key) {
          // Inactive → `undefined`, which the store omits (so an off toggle, whose
          // value is `false`, leaves no `favorite=false` behind).
          state[key] = control.active() ? (control.value() as ParamValue) : undefined;
        }
      }
      const sort = this.sort();
      state[SORT_PARAM] = sort.column;
      state[DIRECTION_PARAM] = sort.column ? sort.direction : undefined;
      const paginator = this.paginator();
      if (paginator) {
        const page = paginator.pageIndex();
        state[PAGE_PARAM] = page > 0 ? page + 1 : undefined;
        const pageSize = paginator.pageSize();
        state[PAGE_SIZE_PARAM] = pageSize === this.baselinePageSize() ? undefined : pageSize;
      }
      this.urlStore.set(state);
    });

    // `<bit-row-group>` only renders in list presentation; warn if declared in table mode.
    effect(() => {
      if (this.presentation() === "table" && this._groups().length > 0) {
        this.logService?.warning(
          'bit-table-v2: `<bit-row-group>` is only supported in `presentation="list"`. ' +
            "Groups are ignored in table presentation.",
        );
      }
    });

    // Only one level of nesting is supported; deeper subgroups are ignored.
    effect(() => {
      const tooDeep = this._groups().some((g) => g.children().some((c) => c.children().length > 0));
      if (tooDeep) {
        this.logService?.warning(
          "bit-table-v2: `<bit-row-group>` supports only one level of nesting; " +
            "groups nested deeper than that are ignored.",
        );
      }
    });

    // The strategy pulls heights lazily, so a collapse (which changes the row count)
    // is handled by CDK's `onDataLengthChanged`. This covers the other case — heights
    // changing with no row-count change (e.g. row height) — which CDK can't observe.
    effect(() => {
      this.itemHeights();
      this.scrollStrategy.refresh();
    });
  }

  /**
   * Applies URL state to sort and pagination once, then opens the write-back gate.
   * Runs before the `defaultSort` fallback so a sorted link wins.
   */
  private restoreFromUrl(): void {
    const fromUrl = this.urlStore();
    const column = fromUrl[SORT_PARAM];
    if (typeof column === "string") {
      const direction = fromUrl[DIRECTION_PARAM] === "desc" ? "desc" : "asc";
      this.sort.set({ column: column as ColumnName<T, S>, direction });
    }
    const paginator = this.paginator();
    if (paginator) {
      // Capture the configured size before any URL override, so write-back can
      // omit `pageSize` whenever it returns to this baseline.
      this.baselinePageSize.set(paginator.pageSize());
      const pageSize = fromUrl[PAGE_SIZE_PARAM];
      if (typeof pageSize === "number" && pageSize > 0) {
        paginator.pageSize.set(pageSize);
      }
      const page = fromUrl[PAGE_PARAM];
      if (typeof page === "number" && page > 0) {
        paginator.pageIndex.set(page - 1);
      }
    }
    this.urlRestored.set(true);
  }

  private readonly _columns = signal<BitColumnComponent[]>([]);

  /**
   * Whether any `<bit-column>` has been projected. When false the table renders in
   * manual mode, which has no sort, selection, filter, or virtualization.
   */
  protected readonly hasColumns = computed(() => this._columns().length > 0);

  /** Registered `<bit-row-group>`s, in declaration order. Empty = ungrouped rendering. */
  private readonly _groups = signal<BitRowGroupComponent<T>[]>([]);

  /**
   * Whether groups actually render. `<bit-row-group>` is `list`-only; in `table`
   * presentation groups are ignored and a warning is logged.
   */
  protected readonly groupingActive = computed(
    () => this.presentation() === "list" && this._groups().length > 0,
  );

  /** Registers a row-group. Called by {@link BitRowGroupComponent} via DI. */
  registerGroup(group: BitRowGroupComponent<T>): void {
    this._groups.update((groups) => [...groups, group]);
  }

  /** @see {@link registerGroup} */
  unregisterGroup(group: BitRowGroupComponent<T>): void {
    this._groups.update((groups) => groups.filter((g) => g !== group));
  }

  /**
   * Header chrome for a group at `level` (0 = top, 1 = subgroup).
   */
  protected groupHeaderClass(level: number, forceHeight: boolean): string {
    if (this.presentation() !== "list") {
      return "tw-flex tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-border-base tw-bg-bg-secondary tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-text-fg-body";
    }
    // Matches the extension's section headers.
    const type =
      level === 0
        ? "tw-text-sm tw-text-fg-heading tw-font-medium"
        : "tw-text-xs tw-text-fg-body-subtle tw-font-medium";
    // A pinned height already spaces the label; bottom padding would only push it off-center.
    const bottom = forceHeight ? "" : " tw-pb-1";
    return `tw-flex tw-items-center tw-px-1 ${type}${bottom}`;
  }

  /** Collapsible-header chrome: the base header plus a full-width hover/focus toggle affordance. */
  protected groupHeaderButtonClass(level: number, forceHeight: boolean): string {
    return (
      this.groupHeaderClass(level, forceHeight) +
      " tw-w-full tw-cursor-pointer tw-rounded tw-border-0 tw-bg-transparent tw-text-start" +
      " hover:tw-bg-hover-default focus-visible:tw-outline-none focus-visible:tw-ring-2" +
      " focus-visible:tw-ring-inset focus-visible:tw-ring-border-focus"
    );
  }

  /**
   * Registered columns resolved against {@link displayedColumns}, omitting any name with
   * no registered `<bit-column>`. What the host allows, before the user's choices narrow it.
   */
  private readonly availableColumns = computed(() => {
    const registered = this._columns();
    const displayed = this.displayedColumns();
    if (!displayed) {
      return registered;
    }
    const registry = new Map(registered.map((c) => [c.name(), c]));
    return displayed
      .map((name) => registry.get(name))
      .filter((c): c is BitColumnComponent => c !== undefined);
  });

  /** The columns the user may toggle: those marked `removable` that have a `label`. */
  private readonly removableColumns = computed(() =>
    this.availableColumns().filter((col) => col.removable() && col.name() && col.label()),
  );

  /** Whether the Customize control applies to this table. */
  readonly canCustomizeColumns = computed(
    () =>
      this.stateKey() != null &&
      this.presentation() === "table" &&
      this.removableColumns().length > 0,
  );

  private readonly columnPreferences = inject(TableColumnPreferencesService);

  private readonly storedHidden = this.columnPreferences.hiddenColumns(this.stateKey);

  /** The stored hidden names, narrowed to columns that are currently togglable. */
  private readonly hiddenColumnNames = computed<ReadonlySet<string>>(() => {
    if (!this.canCustomizeColumns()) {
      return new Set();
    }
    const names = this.removableColumns().map((col) => col.name() ?? "");
    const stored = this.storedHidden();
    // Hold back every removable column until preferences load, so none renders and then vanishes.
    return new Set(stored === undefined ? names : names.filter((name) => stored.has(name)));
  });

  /** {@link availableColumns} minus the columns the user hid. What actually renders. */
  readonly effectiveColumns = computed(() => {
    const hidden = this.hiddenColumnNames();
    const available = this.availableColumns();
    return hidden.size === 0 ? available : available.filter((col) => !hidden.has(col.name() ?? ""));
  });

  /** Total column count including the selection column — the `aria-colspan` a group header spans. */
  protected readonly columnCount = computed(
    () => this.effectiveColumns().length + (this.selectionModel() ? 1 : 0),
  );

  /**
   * Grid-template-columns string derived from the column registry, consumed by
   * `<bit-row>` and `<bit-header-row>`. `undefined` in manual mode.
   */
  readonly gridTemplateColumns = computed<string | undefined>(() => {
    const cols = this.effectiveColumns();
    if (cols.length === 0) {
      return undefined;
    }
    const widths = cols.map((col) => col.width() ?? "1fr");
    // Hiding a column can strip the row of its last flexible track, so the first visible
    // column absorbs the slack.
    if (!widths.some(growsToFill)) {
      widths[0] = grown(widths[0]);
    }
    const parts = this.selectionModel() ? [SELECTION_COLUMN_WIDTH, ...widths] : widths;
    return parts.join(" ");
  });

  /**
   * The table's horizontal scroll position, shared across the header row and the body.
   * {@link SyncScrollLeftDirective} two-way binds each to this signal.
   */
  protected readonly horizontalScroll = signal(0);

  private readonly scrolled = signal(false);

  /** Whether the body has scrolled away from the top. */
  readonly isScrolled = this.scrolled.asReadonly();

  protected onBodyScroll(event: Event): void {
    this.scrolled.set((event.target as HTMLElement).scrollTop > 0);
  }

  /**
   * Keeps the checkbox in sync with the selection, which the bindings alone don't guarantee.
   * Without it the box can end up checked when nothing is selected, or empty when everything is.
   */
  protected onToggleAll(event: Event, sel: TableSelectionModel<T>): void {
    sel.toggleAll();
    const input = event.target as HTMLInputElement;
    input.checked = sel.allSelected();
    input.indeterminate = sel.indeterminate();
  }

  /** Registers a column. Called by {@link BitColumnComponent} via DI. */
  register(col: BitColumnComponent): void {
    this._columns.update((cols) => [...cols, col]);
  }

  /** @see {@link register} */
  unregister(col: BitColumnComponent): void {
    this._columns.update((cols) => cols.filter((c) => c !== col));
  }

  protected readonly isVirtualized = computed(() => this.virtualRowHeight() !== undefined);

  /** True when {@link height} is `"fill"`. */
  protected readonly isFill = computed(() => this.height() === "fill");

  private readonly scrollLayout = inject(ScrollLayoutService);

  // Optional: only the toolbar's Customize button calls this, and the toolbar requires it.
  private readonly dialogService = inject(DialogService, { optional: true });

  /** Opens the Customize columns dialog. */
  openCustomizeColumns(): void {
    this.dialogService?.open<unknown, CustomizeColumnsDialogParams>(
      CustomizeColumnsDialogComponent,
      {
        data: {
          columns: this.removableColumns(),
          hidden: this.hiddenColumnNames(),
          setHidden: (name, hidden) => this.setColumnHidden(name, hidden),
          reset: () => this.resetColumns(),
        },
      },
    );
  }

  /** Shows or hides one column. Idempotent, so callers needn't know the current state. */
  private setColumnHidden(name: string, hidden: boolean): void {
    const key = this.stateKey();
    if (key != null) {
      this.columnPreferences.setColumnHidden(key, name, hidden);
    }
  }

  /** Clears this table's stored preference, restoring the declared column set. */
  private resetColumns(): void {
    const key = this.stateKey();
    if (key != null) {
      this.columnPreferences.reset(key);
    }
  }

  /**
   * The element the body scrolls in, virtualized or not — replaced when the table swaps between
   * them
   */
  private readonly scrollBody = viewChild<ElementRef<HTMLElement> | CdkVirtualScrollViewport>(
    "scrollBody",
  );

  /**
   * Publishes the scrolling body as the page's scroll region while {@link scrollLayoutHost} is set.
   */
  private readonly _scrollLayoutHostEffect = effect((onCleanup) => {
    if (!this.scrollLayoutHost()) {
      return;
    }

    const body = this.scrollBody();
    const element = body instanceof CdkVirtualScrollViewport ? body.elementRef : body;

    if (element == null) {
      return;
    }

    const previous = untracked(() => this.scrollLayout.scrollableRef());
    this.scrollLayout.scrollableRef.set(element);

    onCleanup(() => {
      // Only give the region back if it is still ours; a later host taking over must not be undone.
      if (this.scrollLayout.scrollableRef() === element) {
        this.scrollLayout.scrollableRef.set(previous);
      }
    });
  });

  /** CDK re-measures only on window resize; this viewport also resizes with its surrounding layout. */
  private readonly _viewportResizeEffect = effect((onCleanup) => {
    const body = this.scrollBody();
    if (!(body instanceof CdkVirtualScrollViewport)) {
      return;
    }
    const observer = new ResizeObserver(() => body.checkViewportSize());
    observer.observe(body.elementRef.nativeElement);
    onCleanup(() => observer.disconnect());
  });

  protected readonly isList = computed(() => this.presentation() === "list");

  protected readonly listInset = computed(() => (this.isList() ? "tw-mx-3" : ""));

  /** Uniform height; `--bit-card-gap` removes the card's own margin. */
  protected readonly listCardHeight = computed(() => {
    const advance = this.virtualRowHeight();
    return this.isList() && advance != null ? `calc(${advance}px - var(--bit-card-gap))` : null;
  });

  /** Row-count cap from {@link height} (clamped to a minimum of 4), or undefined when it isn't a number. */
  protected readonly maxRows = computed(() => {
    const h = this.height();
    return typeof h === "number" ? Math.max(4, Math.floor(h)) : undefined;
  });

  /** Outer container chrome: border, rounded corners, subtle shadow. Dropped in `list` presentation so rows float as cards. Becomes a fill flex column when {@link height} is `"fill"`. */
  protected readonly containerClass = computed(() => [
    ...(this.presentation() === "list"
      ? []
      : [
          "tw-bg-bg-primary",
          "tw-border",
          "tw-border-solid",
          "tw-border-border-base",
          "tw-rounded-xl",
          "tw-overflow-clip",
          "tw-shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)]",
        ]),
    ...(this.isFill() ? ["tw-flex", "tw-min-h-0", "tw-flex-1", "tw-flex-col"] : []),
  ]);

  /**
   * {@link filtered} in display order — sorted, but not page-sliced. The selection model scopes over
   * this, so a `max`-capped select-all keeps the rows shown at the top rather than scattered ones.
   */
  readonly sorted = computed<T[]>(() => {
    const filtered = this.filtered();
    const sort = this.sort();
    if (!sort.column) {
      return filtered;
    }
    const col = this.availableColumns().find((c) => c.name() === sort.column);
    return sortRows(filtered, sort.column, sort.direction, sort.fn ?? col?.sortFn());
  });

  /**
   * Rendered rows: {@link sorted} sliced to a projected paginator's page (unless it's in
   * server-side mode, where the data already holds only the page).
   */
  protected readonly rows = computed(() => {
    const sorted = this.sorted();
    const paginator = this.paginator();
    if (paginator && !paginator.manual()) {
      const start = paginator.currentPage() * paginator.pageSize();
      return sorted.slice(start, start + paginator.pageSize());
    }
    return sorted;
  });

  /**
   * The non-virtualized body's render list: {@link rows} as-is when ungrouped, else
   * interleaved headers and rows. A row joins the first group whose `match` claims it;
   * empty groups are skipped unless they clear `hideOnEmpty`, and unclaimed rows trail in
   * a headerless block.
   */
  protected readonly renderItems = computed<RenderItem<T>[]>(() => {
    const rows = this.rows();
    if (!this.groupingActive()) {
      return rows.map((row): RenderItem<T> => ({ kind: "row", row }));
    }

    // First-match-wins partition of `toSplit` across `groups`, preserving row order.
    const partition = (groups: readonly BitRowGroupComponent<T>[], toSplit: readonly T[]) => {
      const buckets = new Map<BitRowGroupComponent<T>, T[]>();
      const unmatched: T[] = [];
      for (const row of toSplit) {
        const group = groups.find((g) => g.match()(row));
        if (!group) {
          unmatched.push(row);
          continue;
        }
        const bucket = buckets.get(group);
        if (bucket) {
          bucket.push(row);
        } else {
          buckets.set(group, [row]);
        }
      }
      return { buckets, unmatched };
    };
    const rowItems = (rs: readonly T[]): RenderItem<T>[] =>
      rs.map((row): RenderItem<T> => ({ kind: "row", row }));

    const items: RenderItem<T>[] = [];
    const top = partition(this._groups(), rows);
    for (const group of this._groups()) {
      const groupRows = top.buckets.get(group) ?? [];
      if (!groupRows.length && group.hideOnEmpty()) {
        continue;
      }
      // A collapsed group still shows its header (with the full count) but hides its body.
      items.push({ kind: "group", group, count: groupRows.length, level: 0 });
      if (group.collapsible() && group.collapsed()) {
        continue;
      }
      // A grid row, so it hides when collapsed — `aria-expanded="false"` has to mean
      // nothing of the group is rendered.
      if (group.description()) {
        items.push({ kind: "groupDescription", group, level: 0 });
      }
      const children = group.children();
      if (children.length === 0) {
        items.push(...rowItems(groupRows));
        continue;
      }
      // One extra level: sub-partition the group's rows across its child groups.
      const sub = partition(children, groupRows);
      // Rows no subgroup claims render flat directly under the parent header, BEFORE
      // the subheadered subgroups — e.g. the extension's flat run followed by a
      // "Cards" subgroup section.
      items.push(...rowItems(sub.unmatched));
      for (const child of children) {
        const childRows = sub.buckets.get(child);
        if (!childRows?.length) {
          continue;
        }
        items.push({ kind: "group", group: child, count: childRows.length, level: 1 });
        if (!(child.collapsible() && child.collapsed())) {
          items.push(...rowItems(childRows));
        }
      }
    }
    items.push(...rowItems(top.unmatched));
    return items;
  });

  /**
   * Per-item pixel heights for {@link TableVirtualScrollStrategy}, in render order.
   * Empty until {@link virtualRowHeight} is set.
   */
  protected readonly itemHeights = computed<number[]>(() => {
    const rowHeight = this.virtualRowHeight();
    if (rowHeight === undefined) {
      return [];
    }
    return this.renderItems().map((item) => {
      if (item.kind === "row") {
        return rowHeight;
      }
      return item.kind === "groupDescription"
        ? this.groupDescriptionHeight
        : this.headerHeight(item.level);
    });
  });

  /** @see {@link GROUP_DESCRIPTION_HEIGHT} */
  protected readonly groupDescriptionHeight = GROUP_DESCRIPTION_HEIGHT;

  /** Fixed virtualized height for a group header at `level` (0 = top, 1 = subgroup). */
  protected headerHeight(level: number): number {
    return level === 0 ? GROUP_HEADER_HEIGHT : SUBGROUP_HEADER_HEIGHT;
  }

  /** Total pixel height of every render item (rows + group headers). */
  protected readonly totalContentHeight = computed(() =>
    this.itemHeights().reduce((sum, height) => sum + height, 0),
  );

  /**
   * trackBy for the render list — a header by its group, a row by the consumer's
   * {@link trackBy}, falling back to row identity.
   */
  protected readonly trackRenderItem: TrackByFunction<RenderItem<T>> = (index, item) => {
    if (item.kind === "group") {
      return item.group;
    }
    if (item.kind === "groupDescription") {
      // Stable and per-group, but not `item.group` — that already keys the header.
      return item.group.headerTemplate();
    }
    const trackBy = this.trackBy();
    return trackBy ? trackBy(index, item.row) : item.row;
  };

  /**
   * Virtual-scroll strategy, provided to the viewport via `VIRTUAL_SCROLL_STRATEGY`.
   * Reads {@link itemHeights} lazily; the constructor effect re-renders on change.
   */
  readonly scrollStrategy = new TableVirtualScrollStrategy(this.itemHeights);

  /** Index array for the skeleton rows shown while loading. */
  protected readonly skeletonRows = computed(() => [...Array(this.loadingRows()).keys()]);

  /** Column-def mode, not loading, with no rows to render (empty or fully filtered out). */
  protected readonly isEmpty = computed(
    () => this.hasColumns() && !this.loading() && this.rows().length === 0,
  );

  /** Empty because filters excluded everything (there is data) vs. genuinely no data. */
  protected readonly noMatches = computed(
    () => this.isEmpty() && this.tableDef().data().length > 0,
  );

  /**
   * Pixel height for the virtual-scroll viewport, capped at {@link maxRows} rows. CDK
   * positions rows absolutely, so they contribute no in-flow height.
   */
  protected readonly viewportHeight = computed<string | undefined>(() => {
    const rowHeight = this.virtualRowHeight();
    if (rowHeight === undefined) {
      return undefined;
    }
    // Content height includes group headers, so grouped tables grow to fit them;
    // the `maxRows` cap stays row-based ("show N rows before scrolling").
    const contentHeight = this.totalContentHeight();
    const maxRows = this.maxRows();
    const height =
      maxRows !== undefined ? Math.min(maxRows * rowHeight, contentHeight) : contentHeight;
    return `${height}px`;
  });

  private readonly logService = inject(LogService, { optional: true });

  ngAfterContentInit(): void {
    if (this._groups().length > 0 && this.paginator()) {
      throw new Error(
        "bit-table-v2: `<bit-row-group>` and `<bit-table-paginator>` are not supported together — " +
          "grouping with pagination is not implemented.",
      );
    }
    if (!this.hasColumns()) {
      if (this.selection()) {
        this.logService?.warning(
          "bit-table-v2: `selection` is configured but no `<bit-column>` was projected. " +
            "Selection requires column-def mode; no checkbox column will render.",
        );
      }
      return;
    }
    if (this.isVirtualized() && this.height() === undefined) {
      this.logService?.warning(
        "bit-table-v2: virtualization (`virtualRowHeight`) needs a bounded height — set `height` to a " +
          'row count or `"fill"` (inside a bounded container). Without one the viewport collapses and no rows render.',
      );
    }
    if (this.isVirtualized() && this.paginator()) {
      this.logService?.warning(
        "bit-table-v2: a paginator and virtualization (`virtualRowHeight`) are mutually exclusive — " +
          "virtualization already renders large sets efficiently.",
      );
    }
    // Restore sort/pagination from the URL (filters restore as their chips
    // register), then open the write-back gate.
    this.restoreFromUrl();

    // Seed the initial sort from the first column declaring `defaultSort`, unless
    // a sort column is already set (e.g. via `[(sort)]` or restored from the URL).
    if (!this.sort().column) {
      const defaultCol = this.availableColumns().find((c) => c.defaultSort());
      const name = defaultCol?.name();
      if (name) {
        this.sort.set({
          column: name as ColumnName<T, S>,
          direction: defaultCol!.defaultSort() ?? "asc",
        });
      }
    }
  }

  /**
   * Cycles the sort on a header click. The column name is type-erased off the
   * projected `<bit-column>`, so it's cast to the typed sort state.
   */
  toggleSort(col: BitColumnComponent): void {
    const name = col.name();
    if (!name) {
      return;
    }
    this.sort.update(
      (current) =>
        cycleSort(current as SortState<string>, name, col.defaultSort() ?? "asc") as SortState<
          ColumnName<T, S>
        >,
    );
  }
}
